import React, {useEffect, useRef, useState} from "react";
import {useTheme} from "@mui/material/styles";
import {LibrettoProjectTypes} from "../../utils/utils";
import {isSupportedChromeEdgeOrBrave, unsupportedEditorBrowserMessage} from "../../utils/browserSupport";
import {useAssetStore} from "../../storage/AssetStore";
import {validateEditMultiSpeakerSelection} from "./EditMultiSpeakerValidation";
import {
    MultiFileReviewState,
    MultiFileSelectState,
    WorkflowUploadHeader,
    WorkflowUploadShell,
    getWorkflowUploadFiletype,
} from "./WorkflowUploadCardShared";

const viewStates = {
    SELECT_FILES: "select-files",
    REVIEW_AND_UPLOAD: "review-and-upload",
};

const EditMultiSpeakerWorkflow = ({
    onClose,
    projectId = null,
    onUploadInitiated,
}) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === "dark";
    const {UploadAsset} = useAssetStore();

    const [selectedFiles, setSelectedFiles] = useState([]);
    const [selectedLanguage, setSelectedLanguage] = useState("auto");
    const [validationMessage, setValidationMessage] = useState("");
    const [warningMessage, setWarningMessage] = useState("");
    const [isStartingUpload, setIsStartingUpload] = useState(false);
    const [currentView, setCurrentView] = useState(viewStates.SELECT_FILES);
    const isMountedRef = useRef(true);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const handleFilesChoose = async (files) => {
        const result = await validateEditMultiSpeakerSelection(files);
        if (result.validationMessage) {
            setValidationMessage(result.validationMessage);
            setWarningMessage("");
            return;
        }

        setSelectedFiles(result.selectedFiles);
        setWarningMessage(result.warningMessage);
        setValidationMessage("");
        setCurrentView(viewStates.REVIEW_AND_UPLOAD);
    };

    const handleUpload = async () => {
        if (selectedFiles.length < 2 || isStartingUpload) return;
        if (!isSupportedChromeEdgeOrBrave()) {
            setValidationMessage(unsupportedEditorBrowserMessage);
            return;
        }

        setIsStartingUpload(true);
        setValidationMessage("");

        try {
            let resolvedProjectId = String(projectId || "").trim();
            const uploadedAssetIds = [];

            for (let i = 0; i < selectedFiles.length; i += 1) {
                const selectedEntry = selectedFiles[i];
                const file = selectedEntry?.file || selectedEntry;
                const assetId = await UploadAsset({
                    projectId: resolvedProjectId || projectId,
                    projectType: LibrettoProjectTypes.ProjectTypeEditor,
                    file,
                    filetype: getWorkflowUploadFiletype(file),
                    languageCode: selectedLanguage,
                    onUploadInitiated: ({projectId: serverProjectId}) => {
                        const nextProjectId = String(serverProjectId || "").trim();
                        if (nextProjectId) {
                            resolvedProjectId = nextProjectId;
                        }
                    },
                });

                if (!assetId) {
                    throw new Error(`Upload failed for ${file?.name || `file ${i + 1}`}`);
                }

                uploadedAssetIds.push(String(assetId || "").trim());
            }

            const safeProjectId = String(resolvedProjectId || "").trim();
            if (!safeProjectId) {
                throw new Error("Missing project id for multi-speaker upload");
            }

            if (onUploadInitiated) {
                onUploadInitiated({
                    projectId: safeProjectId,
                    assetIds: uploadedAssetIds,
                    languageCode: selectedLanguage,
                });
            } else {
                onClose?.();
            }
        } catch (error) {
            console.error("[EditMultiSpeakerWorkflow] Upload failed", error);
            if (isMountedRef.current) {
                setValidationMessage("Could not start upload. Please try again.");
            }
        } finally {
            if (isMountedRef.current) {
                setIsStartingUpload(false);
            }
        }
    };

    const handleSwapFiles = () => {
        setCurrentView(viewStates.SELECT_FILES);
        setValidationMessage("");
        setWarningMessage("");
    };

    return (
        <WorkflowUploadShell>
            <WorkflowUploadHeader
                onClose={onClose}
                title="Edit multi-speaker recording"
                subtitle="Upload separate files from the same conversation to edit them together in one transcript and timeline."
            />

            {currentView === viewStates.SELECT_FILES ? (
                <MultiFileSelectState
                    onChoose={handleFilesChoose}
                    validationMessage={validationMessage}
                    isDarkMode={isDarkMode}
                    accept="video/*,audio/*"
                    uploadTitle="Upload audio or video files"
                    uploadHelperText="Choose at least two files from the same conversation."
                    inputId="edit-multi-speaker-upload"
                />
            ) : null}

            {currentView === viewStates.REVIEW_AND_UPLOAD ? (
                <MultiFileReviewState
                    files={selectedFiles}
                    selectedLanguage={selectedLanguage}
                    setSelectedLanguage={setSelectedLanguage}
                    onSubmit={handleUpload}
                    onSwapFiles={handleSwapFiles}
                    validationMessage={validationMessage}
                    warningMessage={warningMessage}
                    isSubmitting={isStartingUpload}
                    submitLabel="Upload"
                    languageDescription="Set language for the shared transcription."
                />
            ) : null}
        </WorkflowUploadShell>
    );
};

export default EditMultiSpeakerWorkflow;
